import {
  GENERIC_ERROR,
  messageFor,
  toFormErrors,
  withoutErrorsFor,
  type FormErrors,
} from '../../lib/formErrors'

/**
 * Which box on the posting form each rejection belongs to. The unit codes land on the
 * picker they are about — the gate's included, whose subject names what holds the unit
 * back — and the two shared value-object codes (weight, pallets) keep one field each on
 * this form, which is where "one code, one field" has to hold.
 */
const FIELD_BY_CODE: Record<string, string> = {
  availability_truck_required: 'truckId',
  availability_truck_not_dispatchable: 'truckId',
  availability_truck_already_posted: 'truckId',
  availability_trailer_required: 'trailerId',
  availability_trailer_not_dispatchable: 'trailerId',
  availability_trailer_other_carrier: 'trailerId',
  // A straight box truck has no trailer box on the form: the truck is what to change (TRKC-100).
  availability_trailer_with_box_truck: 'truckId',
  // The truck crossed the box-truck line while it was being posted (Copilot on #48).
  availability_truck_changed: 'truckId',
  availability_driver_not_found: 'driverId',
  availability_driver_not_dispatchable: 'driverId',
  availability_driver_other_carrier: 'driverId',
  availability_from_required: 'availableFrom',
  availability_from_in_past: 'availableFrom',
  availability_until_before_from: 'availableUntil',
  availability_city_required: 'city',
  availability_city_too_long: 'city',
  availability_state_invalid: 'state',
  availability_destination_invalid: 'preferredDestinations',
  availability_load_type_required: 'loadType',
  availability_weight_required: 'availableWeight',
  weight_out_of_range: 'availableWeight',
  availability_weight_above_trailer: 'availableWeight',
  availability_weight_above_truck: 'availableWeight',
  pallet_capacity_out_of_range: 'availablePallets',
  availability_pallets_above_trailer: 'availablePallets',
  availability_pallets_above_truck: 'availablePallets',
  availability_reefer_range_invalid: 'reeferMin',
  availability_reefer_range_without_reefer: 'reeferMin',
  availability_reefer_range_required: 'reeferMin',
  availability_securement_invalid: 'securement',
  availability_securement_without_flatbed: 'securement',
  availability_min_rate_invalid: 'minRatePerMile',
  availability_instructions_too_long: 'specialInstructions',
}

/**
 * Complaints that more than one box can fix: a window is mended from either end, a
 * range from either temperature, and a unit's room or gear by picking another trailer.
 */
const CLEARED_BY: Record<string, readonly string[]> = {
  availability_until_before_from: ['availableFrom', 'availableUntil'],
  availability_reefer_range_invalid: ['reeferMin', 'reeferMax'],
  availability_reefer_range_without_reefer: ['reeferMin', 'reeferMax', 'trailerId'],
  availability_reefer_range_required: ['reeferMin', 'reeferMax', 'trailerId'],
  availability_weight_above_trailer: ['availableWeight', 'trailerId'],
  availability_pallets_above_trailer: ['availablePallets', 'trailerId'],
  availability_weight_above_truck: ['availableWeight', 'truckId'],
  availability_pallets_above_truck: ['availablePallets', 'truckId'],
  availability_securement_without_flatbed: ['securement', 'trailerId'],
  availability_trailer_other_carrier: ['trailerId', 'truckId'],
  availability_driver_other_carrier: ['driverId', 'truckId'],
}

export { GENERIC_ERROR, messageFor }
export type { FormErrors }

export const toPostingFormErrors = (caught: unknown): FormErrors => toFormErrors(caught, FIELD_BY_CODE)

export const withoutPostingErrorsFor = (errors: FormErrors, editedField: string): FormErrors =>
  withoutErrorsFor(errors, editedField, CLEARED_BY)
